import { OnboardingErrorMessage } from "../../../models/errors";
import {
  AaaGameConceptData,
  IdXboxGameConceptData,
  PitchConceptData,
  SubFormsDataTypes,
} from "../../../models/forms";
import { IdXboxFormsData } from "../../crm/api/models/forms";
import { AaaAvailabilityDataFilter } from "./aaaGameConcept/aaaAvailabilityDataFilter";
import { AaaConfigurationsDataFilter } from "./aaaGameConcept/aaaConfigurationsDataFilter";
import { AaaDetailsDataFilter } from "./aaaGameConcept/aaaDetailsDataFilter";
import { AaaDistributionInformationDataFilter } from "./aaaGameConcept/aaaDistributionInformationDataFilter";
import { AaaGameExpectationsDataFilter } from "./aaaGameConcept/aaaGameExpectationsDataFilter";
import { AaaSupplementaryDataFilter } from "./aaaGameConcept/aaaSupplementaryDataFilter";
import { AaaTechnicalCapabilitiesDataFilter } from "./aaaGameConcept/aaaTechnicalCapabilitiesDataFilter";
import { AaaUgcDataFilter } from "./aaaGameConcept/aaaUgcDataFilter";
import { AaaXboxNetworkDataFilter } from "./aaaGameConcept/aaaXboxNetworkDataFilter";
import { ConfigurationsDataFilter } from "./gameConcept/configurationsDataFilter";
import { DetailsDataFilter } from "./gameConcept/detailsDataFilter";
import { GameExpectationsDataFilter } from "./gameConcept/gameExpectationsDataFilter";
import { PricingAndAvailabilityDataFilter } from "./gameConcept/pricingAndAvailabilityDataFilter";
import { SupplementaryDataDataFilter } from "./gameConcept/supplementaryDataDataFilter";
import { TechnicalCapabilitiesDataFilter } from "./gameConcept/technicalCapabilitiesDataFilter";
import { UgcDataFilter } from "./gameConcept/ugcDataFilter";
import { XboxNetworkDataFilter } from "./gameConcept/xboxNetworkDataFilter";
import { ContactDataFilter } from "./introduction/contactDataFilter";
import { StudioDataFilter } from "./introduction/studioDataFilter";
import { AboutYourGameDataFilter } from "./pitchConcept/aboutYourGameDataFilter";
import { AboutYourselfDataFilter } from "./pitchConcept/aboutYourselfDataFilter";
import { AskForXboxDataFilter } from "./pitchConcept/askForXboxDataFilter";
import { PublisherDeveloperDataFilter } from "./pitchConcept/publisherDeveloperDataFilter";

export type DataFilterResult = {
  result: boolean;
  message?: OnboardingErrorMessage;
};

export interface IDataFilter {
  validate: (_data: SubFormsDataTypes | undefined) => Promise<DataFilterResult>;
}

export const validateContactInformation = async (
  data: IdXboxFormsData
): Promise<DataFilterResult> => {
  const [studioResult, contactResult] = await Promise.all([
    StudioDataFilter.validate(data.studio),
    ContactDataFilter.validate(data.contact),
  ]);
  return {
    result: studioResult.result && contactResult.result,
    message: studioResult.message || contactResult.message,
  };
};

export const validateGameConcept = async (
  data: IdXboxGameConceptData
): Promise<DataFilterResult> => {
  const [
    detailsResult,
    gameExpectationsResult,
    pricingAndAvailabilityResults,
    technicalCapabilitiesResult,
    configurationsResult,
    xboxNetworkResult,
    ugcResult,
    supplementaryDataResult,
  ] = await Promise.all([
    DetailsDataFilter.validate(data.details),
    GameExpectationsDataFilter.validate(data.gameExpectations),
    PricingAndAvailabilityDataFilter.validate(data.pricingAndAvailability),
    TechnicalCapabilitiesDataFilter.validate(data.technicalCapabilities),
    ConfigurationsDataFilter.validate(data.configurations),
    XboxNetworkDataFilter.validate(data.xboxNetwork),
    UgcDataFilter.validate(data.ugc),
    SupplementaryDataDataFilter.validate(data.supplementaryData),
  ]);
  return {
    result:
      detailsResult.result &&
      gameExpectationsResult.result &&
      pricingAndAvailabilityResults.result &&
      technicalCapabilitiesResult.result &&
      configurationsResult.result &&
      xboxNetworkResult.result &&
      ugcResult.result &&
      supplementaryDataResult.result,
    message:
      detailsResult.message ||
      gameExpectationsResult.message ||
      pricingAndAvailabilityResults.message ||
      technicalCapabilitiesResult.message ||
      configurationsResult.message ||
      xboxNetworkResult.message ||
      ugcResult.message ||
      supplementaryDataResult.message,
  };
};

export const validateAaaGameConcept = async (
  data: AaaGameConceptData
): Promise<DataFilterResult> => {
  const [
    detailsResult,
    distributionInformationResult,
    gameExpectationsResult,
    availabilityResults,
    technicalCapabilitiesResult,
    configurationsResult,
    xboxNetworkResult,
    ugcResult,
    supplementaryDataResult,
  ] = await Promise.all([
    AaaDetailsDataFilter.validate(data.details),
    AaaDistributionInformationDataFilter.validate(data.distributionInformation),
    AaaGameExpectationsDataFilter.validate(data.gameExpectations),
    AaaAvailabilityDataFilter.validate(data.availability),
    AaaTechnicalCapabilitiesDataFilter.validate(data.technicalCapabilities),
    AaaConfigurationsDataFilter.validate(data.configurations),
    AaaXboxNetworkDataFilter.validate(data.xboxNetwork),
    AaaUgcDataFilter.validate(data.ugc),
    AaaSupplementaryDataFilter.validate(data.supplementaryData),
  ]);
  return {
    result:
      detailsResult.result &&
      distributionInformationResult.result &&
      gameExpectationsResult.result &&
      availabilityResults.result &&
      technicalCapabilitiesResult.result &&
      configurationsResult.result &&
      xboxNetworkResult.result &&
      ugcResult.result &&
      supplementaryDataResult.result,
    message:
      detailsResult.message ||
      distributionInformationResult.message ||
      gameExpectationsResult.message ||
      availabilityResults.message ||
      technicalCapabilitiesResult.message ||
      configurationsResult.message ||
      xboxNetworkResult.message ||
      ugcResult.message ||
      supplementaryDataResult.message,
  };
};

export const validatePitchConcept = async (data: PitchConceptData): Promise<DataFilterResult> => {
  const [publisherDeveloperResult, aboutYourGameResult, askForXboxResult, aboutYourselfResult] =
    await Promise.all([
      PublisherDeveloperDataFilter.validate(data.publisherDeveloperInformation),
      AboutYourGameDataFilter.validate(data.aboutYourGame),
      AskForXboxDataFilter.validate(data.askForXbox),
      AboutYourselfDataFilter.validate(data.aboutYourself),
    ]);
  return {
    result:
      publisherDeveloperResult.result &&
      aboutYourGameResult.result &&
      askForXboxResult.result &&
      aboutYourselfResult.result,
    message:
      publisherDeveloperResult.message ||
      aboutYourGameResult.message ||
      askForXboxResult.message ||
      aboutYourselfResult.message,
  };
};
